import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, UploadIcon, FileTextIcon, XIcon, CheckCircleIcon, LoaderIcon } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { mockSuppliers } from '../utils/mockSuppliers';
import { mockBills } from '../utils/mockBills';
const CURRENCIES = ['THB', 'USD', 'EUR', 'GBP', 'SGD', 'MYR'];
interface UploadedFile {
  name: string;
  size: number;
  type: string;
}
interface ExtractedData {
  invoiceNumber: string;
  localId: string;
  dueDate: string;
  amountDue: string;
  supplierName: string;
}
export function BillForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const billId = searchParams.get('id');
  const prefillSupplierId = searchParams.get('supplierId');
  const isEditMode = !!billId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editingBill = isEditMode ? mockBills.find(b => b.id === billId) || null : null;
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: editingBill?.billNumber || '',
    supplierName: editingBill?.supplierName || '',
    localId: '',
    dueDate: editingBill?.dueDate || '',
    amountDue: editingBill?.amount?.toString() || '',
    currency: editingBill?.currency || 'THB'
  });
  // Pre-fill supplier from query param
  useEffect(() => {
    if (prefillSupplierId && !isEditMode) {
      const supplier = mockSuppliers.find(s => s.id === prefillSupplierId);
      if (supplier) {
        setFormData(prev => ({
          ...prev,
          supplierName: supplier.name
        }));
      }
    }
  }, [prefillSupplierId, isEditMode]);
  const supplierOptions = mockSuppliers.map(s => s.name);
  // Simulate OCR extraction from uploaded file
  const simulateExtraction = useCallback((fileName: string) => {
    setIsExtracting(true);
    setExtractionDone(false);
    // Simulate a delay for "processing"
    setTimeout(() => {
      // Mock extracted data based on file name patterns
      const mockExtracted: ExtractedData = {
        invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
        localId: `LOC-${Math.floor(Math.random() * 90000 + 10000)}`,
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        amountDue: '',
        supplierName: ''
      };
      setFormData(prev => ({
        ...prev,
        invoiceNumber: prev.invoiceNumber || mockExtracted.invoiceNumber,
        localId: prev.localId || mockExtracted.localId,
        dueDate: prev.dueDate || mockExtracted.dueDate
      }));
      setIsExtracting(false);
      setExtractionDone(true);
    }, 1800);
  }, []);
  const handleFileSelect = (file: File) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) return;
    setUploadedFile({
      name: file.name,
      size: file.size,
      type: file.type
    });
    simulateExtraction(file.name);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [simulateExtraction]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const removeFile = () => {
    setUploadedFile(null);
    setExtractionDone(false);
    setIsExtracting(false);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/bills');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  return <div className="max-w-xl mx-auto pb-12" data-id="element-5509">
      {/* Header */}
      <div className="mb-6" data-id="element-5510">
        <button onClick={() => navigate('/bills')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-5511">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-5512" />
          Back to Bills
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5513">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-5514">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-5515">
            {isEditMode ? 'Edit bill' : 'Add bill'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-5516">
            Upload your invoice and we'll extract the details automatically.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6" data-id="element-5517">
          {/* File Upload Area */}
          {!uploadedFile ? <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${isDragOver ? 'border-[#F7941D] bg-orange-50/50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`} data-id="element-5518">
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }} data-id="element-5519" />
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors ${isDragOver ? 'bg-[#F7941D]/10' : 'bg-gray-100'}`} data-id="element-5520">
                <UploadIcon size={22} className={isDragOver ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-5521" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-5522">
                Drag & drop your files here
              </p>
              <p className="text-xs text-gray-500" data-id="element-5523">
                Supported file types PDF, JPG, PNG
              </p>
            </div> /* Uploaded File Card */ : <div className="border border-gray-200 rounded-xl p-4" data-id="element-5524">
              <div className="flex items-center gap-3" data-id="element-5525">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-5526">
                  <FileTextIcon size={20} className="text-red-500" data-id="element-5527" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-5528">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-5529">
                    {uploadedFile.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-5530">
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
                {isExtracting ? <div className="flex items-center gap-2 text-[#F7941D]" data-id="element-5531">
                    <LoaderIcon size={16} className="animate-spin" data-id="element-5532" />
                    <span className="text-xs font-medium" data-id="element-5533">Extracting…</span>
                  </div> : extractionDone ? <div className="flex items-center gap-1.5 text-green-600" data-id="element-5534">
                    <CheckCircleIcon size={16} data-id="element-5535" />
                    <span className="text-xs font-medium" data-id="element-5536">Extracted</span>
                  </div> : null}
                <button type="button" onClick={removeFile} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-5537">
                  <XIcon size={16} data-id="element-5538" />
                </button>
              </div>

              {/* Extraction progress bar */}
              {isExtracting && <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-5539">
                  <div className="h-full bg-gradient-to-r from-[#F7941D] to-[#F9A826] rounded-full animate-pulse w-2/3" data-id="element-5540" />
                </div>}
            </div>}

          {/* Extracted fields hint */}
          {extractionDone && <div className="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3 flex items-start gap-2.5" data-id="element-5541">
              <CheckCircleIcon size={16} className="text-[#F7941D] mt-0.5 flex-shrink-0" data-id="element-5542" />
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-5543">
                Invoice number, local ID, and due date were extracted from your
                file. You can edit them below if needed.
              </p>
            </div>}

          {/* Core Fields — always visible */}
          <div className="space-y-5" data-id="element-5544">
            {/* Supplier — always required */}
            <div data-id="element-5545">
              <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5546">
                Supplier <span className="text-red-400" data-id="element-5547">*</span>
              </label>
              <SearchableSelect name="supplierName" options={supplierOptions} value={formData.supplierName} onChange={val => setFormData(prev => ({
              ...prev,
              supplierName: val
            }))} placeholder="Select supplier" data-id="element-5548" />
            </div>

            {/* Amount + Currency side by side */}
            <div className="grid grid-cols-3 gap-3" data-id="element-5549">
              <div className="col-span-2" data-id="element-5550">
                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5551">
                  Amount due <span className="text-red-400" data-id="element-5552">*</span>
                </label>
                <input type="number" name="amountDue" step="0.01" min="0" value={formData.amountDue} onChange={handleChange} placeholder="0.00" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" required data-id="element-5553" />
              </div>
              <div data-id="element-5554">
                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5555">
                  Currency
                </label>
                <SearchableSelect name="currency" options={CURRENCIES} value={formData.currency} onChange={val => setFormData(prev => ({
                ...prev,
                currency: val
              }))} placeholder="THB" data-id="element-5556" />
              </div>
            </div>

            {/* Due date */}
            <div data-id="element-5557">
              <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5558">
                Due date <span className="text-red-400" data-id="element-5559">*</span>
              </label>
              <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" required data-id="element-5560" />
            </div>
          </div>

          {/* Auto-extracted fields — shown after extraction or always editable */}
          {(extractionDone || isEditMode) && <div className="border-t border-gray-100 pt-5 space-y-5" data-id="element-5561">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-5562">
                Extracted details
              </p>
              <div className="grid grid-cols-2 gap-3" data-id="element-5563">
                <div data-id="element-5564">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5565">
                    Invoice number
                  </label>
                  <input type="text" name="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} placeholder="Auto-detected" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-5566" />
                </div>
                <div data-id="element-5567">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5568">
                    Local ID
                  </label>
                  <input type="text" name="localId" value={formData.localId} onChange={handleChange} placeholder="Auto-detected" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-5569" />
                </div>
              </div>
            </div>}

          {/* Actions */}
          <div className="pt-4 flex justify-end gap-3" data-id="element-5570">
            <button type="button" onClick={() => navigate('/bills')} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5571">
              Cancel
            </button>
            <button type="submit" disabled={!formData.supplierName || !formData.amountDue} className="px-6 py-2.5 text-sm font-medium text-white bg-[#F7941D] rounded-lg hover:bg-[#D97B06] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5572">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>;
}